import React, { useMemo } from "react"
import { ProductPageFunnelData } from "../connect"
import FunnelChart, {
  FunnelStage,
} from "~/ui-rtk/components/charts/FunnelChart"
import { Loader } from "~/ui-rtk/components/ui/common"
import BenchmarkStatCard from "~/ui-rtk/components/ui/charts/BenchmarkStatCard"
import { FunnelBenchmarks, type PlottedRow } from "../../types"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export interface ConversionFlowStep {
  label: string
  value: number
  conversionRate?: number
  abandonmentRate?: number
  lostCount?: number
}

interface ProductPageFunnelChartProps {
  data: ProductPageFunnelData | null
  compareData?: ProductPageFunnelData | null
  conversionFlowSteps?: ConversionFlowStep[]
  showSummaryStats?: boolean
  showConversionFlow?: boolean
  isLoading?: boolean
  benchmarks?: FunnelBenchmarks | null
  showBenchmarks?: boolean
  plottedRows?: PlottedRow[]
}

export default function ProductPageFunnelChart({
  data,
  compareData,
  conversionFlowSteps,
  showSummaryStats = true,
  showConversionFlow = true,
  isLoading = false,
  benchmarks,
  showBenchmarks = false,
  plottedRows = [],
}: ProductPageFunnelChartProps) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const funnelStages = useMemo<FunnelStage[]>(() => {
    if (!data) return []

    return [
      {
        label: "Product Views",
        value: data.productViewers,
        percentage: 100,
        abandonmentCount: 0,
        abandonmentRate: 0,
        avgOrderValue: data.avgOrderValue,
      },
      {
        label: "Add to Cart",
        value: data.addersToCart,
        percentage: data.productToCartConversion,
        abandonmentCount: data.productViewers - data.addersToCart,
        abandonmentRate: data.productToCartAbandon,
        revenue: data.revenueAtCart,
        avgOrderValue: data.avgOrderValue,
      },
      {
        label: "Checkout",
        value: data.checkouts,
        percentage:
          data.productViewers > 0
            ? (data.checkouts / data.productViewers) * 100
            : 0,
        abandonmentCount: data.addersToCart - data.checkouts,
        abandonmentRate: data.cartToCheckoutAbandon,
        revenue: data.revenueAtCheckout,
        avgOrderValue: data.avgOrderValue,
      },
      {
        label: "Purchases",
        value: data.purchasers,
        percentage: data.productToPurchaseConversion,
        abandonmentCount: data.checkouts - data.purchasers,
        abandonmentRate: data.checkoutToPurchaseAbandon,
        revenue: data.revenueAtPurchase,
        avgOrderValue: data.avgOrderValue,
        firstOrders: data.firstOrders,
        repeatOrders: data.repeatOrders,
      },
    ]
  }, [data])

  const compareFunnelStages = useMemo<FunnelStage[] | undefined>(() => {
    if (!compareData) return undefined

    return [
      {
        label: "Product Views",
        value: compareData.productViewers,
        percentage: 100,
        abandonmentCount: 0,
        abandonmentRate: 0,
        avgOrderValue: compareData.avgOrderValue,
      },
      {
        label: "Add to Cart",
        value: compareData.addersToCart,
        percentage: compareData.productToCartConversion,
        abandonmentCount: compareData.productViewers - compareData.addersToCart,
        abandonmentRate: compareData.productToCartAbandon,
        revenue: compareData.revenueAtCart,
        avgOrderValue: compareData.avgOrderValue,
      },
      {
        label: "Checkout",
        value: compareData.checkouts,
        percentage:
          compareData.productViewers > 0
            ? (compareData.checkouts / compareData.productViewers) * 100
            : 0,
        abandonmentCount: compareData.addersToCart - compareData.checkouts,
        abandonmentRate: compareData.cartToCheckoutAbandon,
        revenue: compareData.revenueAtCheckout,
        avgOrderValue: compareData.avgOrderValue,
      },
      {
        label: "Purchases",
        value: compareData.purchasers,
        percentage: compareData.productToPurchaseConversion,
        abandonmentCount: compareData.checkouts - compareData.purchasers,
        abandonmentRate: compareData.checkoutToPurchaseAbandon,
        revenue: compareData.revenueAtPurchase,
        avgOrderValue: compareData.avgOrderValue,
        firstOrders: compareData.firstOrders,
        repeatOrders: compareData.repeatOrders,
      },
    ]
  }, [compareData])

  // Default conversion flow steps for 4-step funnel
  const defaultConversionFlowSteps = useMemo<ConversionFlowStep[]>(() => {
    if (!data) return []

    return [
      {
        label: "Product Views",
        value: data.productViewers,
      },
      {
        label: "Add to Cart",
        value: data.addersToCart,
        conversionRate: data.productToCartConversion,
        abandonmentRate: data.productToCartAbandon,
        lostCount: data.productViewers - data.addersToCart,
      },
      {
        label: "Checkout",
        value: data.checkouts,
        conversionRate: data.cartToCheckoutConversion,
        abandonmentRate: data.cartToCheckoutAbandon,
        lostCount: data.addersToCart - data.checkouts,
      },
      {
        label: "Purchases",
        value: data.purchasers,
        conversionRate: data.checkoutToPurchaseConversion,
        abandonmentRate: data.checkoutToPurchaseAbandon,
        lostCount: data.checkouts - data.purchasers,
      },
    ]
  }, [data])

  // Compute plotted-row-based conversion rates when rows are plotted
  // Product stages: [0] Product Views, [1] Add to Cart, [2] Checkout, [3] Purchases
  const plottedStats = useMemo(() => {
    if (plottedRows.length === 0) return null
    const sumViews = plottedRows.reduce(
      (s, r) => s + (r.stages[0]?.value ?? 0),
      0,
    )
    const sumCart = plottedRows.reduce(
      (s, r) => s + (r.stages[1]?.value ?? 0),
      0,
    )
    const sumCheckout = plottedRows.reduce(
      (s, r) => s + (r.stages[2]?.value ?? 0),
      0,
    )
    const sumPurchases = plottedRows.reduce(
      (s, r) => s + (r.stages[3]?.value ?? 0),
      0,
    )
    return {
      productToCart: sumViews > 0 ? (sumCart / sumViews) * 100 : 0,
      cartToCheckout: sumCart > 0 ? (sumCheckout / sumCart) * 100 : 0,
      checkoutToPurchase:
        sumCheckout > 0 ? (sumPurchases / sumCheckout) * 100 : 0,
      productToPurchase: sumViews > 0 ? (sumPurchases / sumViews) * 100 : 0,
    }
  }, [plottedRows])

  const flowStepsToRender = conversionFlowSteps || defaultConversionFlowSteps

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size={50} />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-96 text-system-gray-400">
        No data available for the selected time period.
      </div>
    )
  }

  const hasData =
    data.productViewers > 0 ||
    data.addersToCart > 0 ||
    data.checkouts > 0 ||
    data.purchasers > 0

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-system-gray-400 text-lg mb-2">
            No funnel data available
          </p>
          <p className="text-system-gray-500 text-sm">
            Try adjusting your date range or filters
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Summary Stats with Benchmark Bars */}
      {showSummaryStats && data && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <BenchmarkStatCard
            label="Product → Cart"
            currentValue={
              plottedStats?.productToCart ?? data.productToCartConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.productToCart
                : data.productToCartAbandon
            }
            benchmark={benchmarks?.product_to_cart}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
          <BenchmarkStatCard
            label="Cart → Checkout"
            currentValue={
              plottedStats?.cartToCheckout ?? data.cartToCheckoutConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.cartToCheckout
                : data.cartToCheckoutAbandon
            }
            benchmark={benchmarks?.cart_to_checkout}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
          <BenchmarkStatCard
            label="Checkout → Purchase"
            currentValue={
              plottedStats?.checkoutToPurchase ??
              data.checkoutToPurchaseConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.checkoutToPurchase
                : data.checkoutToPurchaseAbandon
            }
            benchmark={benchmarks?.checkout_to_purchase}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
          <BenchmarkStatCard
            label="Product → Purchase"
            currentValue={
              plottedStats?.productToPurchase ??
              data.productToPurchaseConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.productToPurchase
                : data.productToPurchaseAbandon
            }
            benchmark={benchmarks?.product_to_purchase}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
        </div>
      )}

      {/* Reusable Funnel Chart */}
      <FunnelChart
        stages={funnelStages}
        compareStages={compareFunnelStages}
        plottedRows={plottedRows}
      />

      {/* Legend for plotted rows */}
      {plottedRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-6 mt-4 px-2">
          {plottedRows.map((row, idx) => (
            <div
              key={row.id || `legend-${idx}`}
              className="flex items-center gap-2"
            >
              <div
                className="w-8 h-2 rounded"
                style={{ backgroundColor: row.color }}
              />
              <span
                className={cn(
                  "text-sm text-system-gray-400",
                  isDemoEnabled && "blur-md",
                )}
              >
                {row.label}
              </span>
            </div>
          ))}
          {compareFunnelStages && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 rounded border-t-2 border-dashed border-white" />
              <span className="text-sm text-system-gray-400">
                Comparison Period
              </span>
            </div>
          )}
        </div>
      )}

      {/* Conversion Flow Summary */}
      {showConversionFlow && flowStepsToRender.length > 0 && (
        <div className="flex items-center justify-around mt-6 text-sm">
          {flowStepsToRender.map((step, index) => {
            const nextStep = flowStepsToRender[index + 1]
            return (
              <React.Fragment key={`flow-step-${index}`}>
                {/* Step */}
                <div className="text-center flex-1">
                  <div className="font-semibold text-white text-lg">
                    {step.value.toLocaleString()}
                  </div>
                  <div className="text-system-gray-400">{step.label}</div>
                </div>

                {/* Arrow between steps */}
                {index < flowStepsToRender.length - 1 && nextStep && (
                  <div className="text-center px-4">
                    <div className="text-white text-xl">→</div>
                    {nextStep.abandonmentRate !== undefined && (
                      <div className="text-xs text-green-400 font-semibold mt-1">
                        {(100 - nextStep.abandonmentRate).toFixed(1)}%
                        conversion
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}
